import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';
import { companyInfo, contactInfo } from '../../data/mock';

const footerLinks = {
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Our Team', href: '/about#team' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ],
  services: [
    { name: 'Export Services', href: '/services' },
    { name: 'Sourcing', href: '/services#sourcing' },
    { name: 'Logistics', href: '/services#logistics' },
    { name: 'Compliance', href: '/compliance' },
  ],
  resources: [
    { name: 'Products', href: '/products' },
    { name: 'Markets', href: '/markets' },
    { name: 'Insights', href: '/insights' },
    { name: 'FAQ', href: '/faq' },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-navy-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo-dark.png" 
                alt="Naviton Global" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-slate-300 mb-6 max-w-sm leading-relaxed">
              Connecting Australian quality to global markets. Your trusted partner for 
              international trade, premium sourcing, and seamless logistics coordination.
            </p>
            <div className="flex gap-4">
              <a
                href="#"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 text-slate-300" />
              </a>
              <a
                href="#"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5 text-slate-300" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-teal-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-teal-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-white mb-4">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{contactInfo.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{contactInfo.phone}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm whitespace-pre-line">
                  {contactInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Acknowledgement of Country */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h4 className="text-sm font-semibold text-teal-400 mb-3">Acknowledgement of Country</h4>
          <p className="text-slate-400 text-sm leading-relaxed">
            Naviton Global acknowledges the Gadigal people of the Eora Nation as the Traditional Custodians of the land on which we operate. We pay our respects to Elders past, present and emerging, and extend that respect to all Aboriginal and Torres Strait Islander peoples.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Naviton Global Pty Ltd. All rights reserved.
            </div>
            <div className="text-slate-400 text-sm">
              ABN: {companyInfo.abn}
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-slate-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-teal-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
